import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminOverview from '../components/admin/AdminOverview';
import AdminIntegrations from '../components/admin/AdminIntegrations';
import AdminUsers from '../components/admin/AdminUsers';
import AdminRegions from '../components/admin/AdminRegions';
import AdminFlags from '../components/admin/AdminFlags';
import { getHealth } from '../components/admin/health';
import { Button, ConfirmProvider, ToastProvider, timeAgo, useAdminApi } from '../components/admin/ui';

const TABS = [
  ['overview', 'Overview'],
  ['integrations', 'Integrations'],
  ['users', 'Users'],
  ['regions', 'Regions'],
  ['flags', 'Listing flags'],
];

function AdminDashboard() {
  const api = useAdminApi();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [, forceTick] = useState(0);

  // Filters / drawer requested from another tab (e.g. clicking "Failing" on the overview)
  const [integrationPreset, setIntegrationPreset] = useState(null);
  const [userPreset, setUserPreset] = useState(null);
  const [openUuid, setOpenUuid] = useState(null);

  const tab = TABS.some(([id]) => id === location.hash.slice(1)) ? location.hash.slice(1) : 'overview';

  const goTo = useCallback((nextTab, preset, uuid) => {
    if (nextTab === 'integrations') setIntegrationPreset(preset ? { ...preset } : { health: 'all' });
    if (nextTab === 'users') setUserPreset(preset ? { ...preset } : { filter: 'all' });
    setOpenUuid(uuid || null);
    navigate(`#${nextTab}`);
  }, [navigate]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadError('');
    setRefreshing(true);
    try {
      setData(await api('/admin/'));
      setUpdatedAt(Date.now());
    } catch (error) {
      if (error.status === 403) {
        navigate('/dashboard');
        return;
      }
      // A failed silent refresh shouldn't wipe a working page
      if (!silent) setLoadError(error.message);
    } finally {
      setRefreshing(false);
    }
  }, [api, navigate]);

  useEffect(() => {
    document.title = 'Admin - Zeitgeists';
    load();
  }, [load]);

  // Keep the "updated 3m ago" label fresh
  useEffect(() => {
    const timer = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  const attentionCount = useMemo(() => {
    if (!data) return 0;
    return data.placeIntegrations.filter((integration) => ['failing', 'stale', 'new'].includes(getHealth(integration).key)).length;
  }, [data]);

  const clearOpenUuid = useCallback(() => setOpenUuid(null), []);

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto px-4 mt-36">
        {loadError ? (
          <div className="border border-red-300 bg-red-50 rounded-lg p-6 max-w-lg">
            <p className="font-semibold text-red-800">Couldn’t load the admin data</p>
            <p className="text-sm text-red-700 mt-1">{loadError}</p>
            <Button className="mt-4" onClick={() => load()}>Try again</Button>
          </div>
        ) : (
          <p className="text-gray-500">Loading admin…</p>
        )}
      </div>
    );
  }

  const badge = { integrations: attentionCount, users: data.users.filter((u) => !u.isConfirmed).length, flags: data.openFlagCount || 0 };

  return (
    <div className="max-w-7xl mx-auto px-4 pb-16 mt-36">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold">Admin</h1>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          {updatedAt && <span>Updated {timeAgo(updatedAt)}</span>}
          <Button busy={refreshing} onClick={() => load({ silent: true })}>Refresh</Button>
        </div>
      </div>

      <nav className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto" aria-label="Admin sections">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            onClick={() => goTo(id, id === 'integrations' ? { health: 'all' } : id === 'users' ? { filter: 'all' } : undefined)}
            className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 whitespace-nowrap ${tab === id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          >
            {label}
            {badge[id] > 0 && (
              <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${id === 'integrations' || id === 'flags' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>{badge[id]}</span>
            )}
          </button>
        ))}
      </nav>

      {tab === 'overview' && <AdminOverview data={data} goTo={goTo} reload={load} />}
      {tab === 'integrations' && (
        <AdminIntegrations data={data} reload={load} preset={integrationPreset} openUuid={openUuid} onOpened={clearOpenUuid} />
      )}
      {tab === 'users' && <AdminUsers data={data} reload={load} preset={userPreset} />}
      {tab === 'regions' && <AdminRegions data={data} reload={load} />}
      {tab === 'flags' && <AdminFlags regions={data.regions} reload={load} />}
    </div>
  );
}

function Admin() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AdminDashboard />
      </ConfirmProvider>
    </ToastProvider>
  );
}

export default Admin;
